import type {
	AccountResponse,
	ApiKeyCreatedResponse,
	ApiKeyResponse,
	GroupResponse,
} from "@ccflare/api";
import { HttpClient, HttpError } from "@ccflare/http";
import type {
	AccountCreateData,
	AccountProvider,
	AnalyticsResponse,
	ApiKeyProvider,
	AuthCompleteData,
	AuthInitData,
	AuthSessionStatusResponse,
	CleanupResponse,
	LogEvent,
	MutationResult,
	OAuthProvider,
	RequestPayload,
	RequestSummary,
	RetentionGetResponse,
	RetentionSetRequest,
	StatsWithAccounts,
	StrategyName,
	StrategyResponse,
	TimeRange,
} from "@ccflare/types";
import { isLogEvent, parseLogStreamEvent } from "@ccflare/types";
import { API_LIMITS, API_TIMEOUT } from "./constants";

export interface FxRateResponse {
	base: string;
	currency: string;
	symbol: string;
	rate: number;
	asOf: string;
}

class API extends HttpClient {
	private unauthorizedListeners: Array<() => void> = [];

	constructor() {
		super({
			baseUrl: "",
			defaultHeaders: {
				"Content-Type": "application/json",
			},
			timeout: API_TIMEOUT,
			retries: 1,
			onUnauthorized: () => {
				for (const fn of this.unauthorizedListeners) {
					fn();
				}
			},
		});
	}

	onUnauthorized(fn: () => void): () => void {
		this.unauthorizedListeners.push(fn);
		return () => {
			this.unauthorizedListeners = this.unauthorizedListeners.filter(
				(l) => l !== fn,
			);
		};
	}

	async getGatewayStatus(): Promise<{
		gatewayMode: boolean;
		loginMode?: "local" | "azure";
		oauthCatcherAvailable?: boolean;
	}> {
		return this.getJson<{
			gatewayMode: boolean;
			loginMode?: "local" | "azure";
			oauthCatcherAvailable?: boolean;
		}>("/api/gateway-status");
	}

	async login(credentials: {
		username: string;
		password: string;
	}): Promise<{ username: string; role: string }> {
		try {
			return await this.postJson<{ username: string; role: string }>(
				"/api/auth/login",
				credentials,
			);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async logout(): Promise<void> {
		try {
			await this.postJson("/api/auth/logout");
		} catch {
			// ignore errors on logout
		}
	}

	async getMe(): Promise<{ id: string; username: string; role: string }> {
		return this.getJson<{ id: string; username: string; role: string }>(
			"/api/auth/me",
		);
	}

	async listApiKeys(): Promise<ApiKeyResponse[]> {
		return this.getJson<ApiKeyResponse[]>("/api/keys");
	}

	async createApiKey(
		name: string,
		groupId?: string,
	): Promise<ApiKeyCreatedResponse> {
		try {
			return await this.postJson<ApiKeyCreatedResponse>("/api/keys", {
				name,
				...(groupId ? { groupId } : {}),
			});
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async setApiKeyActive(id: string, active: boolean): Promise<void> {
		try {
			await this.patchJson(`/api/keys/${id}`, { active });
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async setApiKeyGroup(id: string, groupId: string): Promise<void> {
		try {
			await this.patchJson(`/api/keys/${id}`, { groupId });
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async deleteApiKey(id: string): Promise<void> {
		try {
			await this.deleteJson(`/api/keys/${id}`);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async getStats(): Promise<StatsWithAccounts> {
		return this.getJson<StatsWithAccounts>("/api/stats");
	}

	async getAccounts(): Promise<AccountResponse[]> {
		return this.getJson<AccountResponse[]>("/api/accounts");
	}

	async getFxRate(): Promise<FxRateResponse> {
		return this.getJson<FxRateResponse>("/api/fx");
	}

	async createApiKeyAccount(data: {
		name: string;
		provider: ApiKeyProvider;
		apiKey: string;
	}): Promise<{ message: string; accountId: string }> {
		try {
			const result = await this.postJson<MutationResult<AccountCreateData>>(
				"/api/accounts",
				{
					name: data.name,
					provider: data.provider,
					auth_method: "api_key",
					api_key: data.apiKey,
				},
			);
			return {
				message: result.message,
				accountId: result.data?.accountId ?? "",
			};
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	/**
	 * Start an OAuth flow. With `accountId` it runs in re-auth mode: the
	 * existing account is logged in again in place (id/name/group/stats kept).
	 */
	async initAddAccount(data: {
		name: string;
		provider: OAuthProvider;
		accountId?: string;
	}): Promise<{ authUrl: string; sessionId: string }> {
		try {
			const result = await this.postJson<MutationResult<AuthInitData>>(
				`/api/auth/${data.provider}/init`,
				data,
			);
			return {
				authUrl: result.data?.authUrl ?? "",
				sessionId: result.data?.sessionId ?? "",
			};
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async completeAddAccount(data: {
		provider: OAuthProvider;
		sessionId: string;
		code: string;
	}): Promise<{ message: string; provider: OAuthProvider }> {
		try {
			const result = await this.postJson<MutationResult<AuthCompleteData>>(
				`/api/auth/${data.provider}/complete`,
				data,
			);
			return {
				message: result.message,
				provider: result.data?.provider ?? data.provider,
			};
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async getAuthSessionStatus(
		sessionId: string,
	): Promise<AuthSessionStatusResponse> {
		try {
			return await this.getJson<AuthSessionStatusResponse>(
				`/api/auth/session/${sessionId}/status`,
			);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async removeAccount(id: string): Promise<void> {
		try {
			await this.deleteJson(`/api/accounts/${id}`);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async resetStats(): Promise<void> {
		await this.postJson("/api/stats/reset");
	}

	async getLogHistory(): Promise<LogEvent[]> {
		return this.getJson<LogEvent[]>("/api/logs/history");
	}

	// SSE streaming requires special handling, keep as-is
	streamLogs(onLog: (log: LogEvent) => void): EventSource {
		const eventSource = new EventSource(`/api/logs/stream`);
		eventSource.addEventListener("message", (event) => {
			try {
				const data = parseLogStreamEvent(JSON.parse(event.data));
				if (data && isLogEvent(data)) {
					onLog(data);
				}
			} catch (e) {
				console.error("Error parsing log event:", e);
			}
		});
		return eventSource;
	}

	async getRequestsDetail(
		limit: number = API_LIMITS.requestsDetail,
	): Promise<RequestPayload[]> {
		return this.getJson<RequestPayload[]>(
			`/api/requests/detail?limit=${limit}`,
		);
	}

	async getRequestsSummary(
		limit: number = API_LIMITS.requestsSummary,
	): Promise<RequestSummary[]> {
		return this.getJson<RequestSummary[]>(`/api/requests?limit=${limit}`);
	}

	async getRequestConversation(requestId: string): Promise<RequestPayload[]> {
		return this.getJson<RequestPayload[]>(
			`/api/requests/${encodeURIComponent(requestId)}/conversation`,
		);
	}

	async getRequestPayload(requestId: string): Promise<RequestPayload> {
		return this.getJson<RequestPayload>(
			`/api/requests/${encodeURIComponent(requestId)}`,
		);
	}

	async getAnalytics(
		range: TimeRange = "24h",
		filters?: {
			providers?: AccountProvider[];
			accounts?: string[];
			models?: string[];
			status?: "all" | "success" | "error";
			apiKeys?: string[];
		},
		mode: "normal" | "cumulative" = "normal",
		modelBreakdown?: boolean,
	): Promise<AnalyticsResponse> {
		const params = new URLSearchParams({ range });

		if (filters?.providers?.length) {
			params.append("providers", filters.providers.join(","));
		}
		if (filters?.accounts?.length) {
			params.append("accounts", filters.accounts.join(","));
		}
		if (filters?.models?.length) {
			params.append("models", filters.models.join(","));
		}
		if (filters?.status && filters.status !== "all") {
			params.append("status", filters.status);
		}
		if (filters?.apiKeys?.length) {
			params.append("apiKeys", filters.apiKeys.join(","));
		}
		if (mode === "cumulative") {
			params.append("mode", "cumulative");
		}
		if (modelBreakdown) {
			params.append("modelBreakdown", "true");
		}

		return this.getJson<AnalyticsResponse>(`/api/analytics?${params}`);
	}

	async pauseAccount(accountId: string): Promise<void> {
		try {
			await this.postJson(`/api/accounts/${accountId}/pause`);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async resumeAccount(accountId: string): Promise<void> {
		try {
			await this.postJson(`/api/accounts/${accountId}/resume`);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async renameAccount(
		accountId: string,
		newName: string,
	): Promise<{ newName: string }> {
		try {
			const result = await this.postJson<MutationResult<{ newName: string }>>(
				`/api/accounts/${accountId}/rename`,
				{ name: newName },
			);
			return { newName: result.data?.newName ?? newName };
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async setAccountGroup(accountId: string, groupId: string): Promise<void> {
		try {
			await this.patchJson(`/api/accounts/${accountId}`, { groupId });
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async listGroups(): Promise<GroupResponse[]> {
		return this.getJson<GroupResponse[]>("/api/groups");
	}

	async createGroup(data: {
		name: string;
		description?: string;
	}): Promise<GroupResponse> {
		try {
			return await this.postJson<GroupResponse>("/api/groups", data);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async updateGroup(
		id: string,
		data: { name?: string; description?: string },
	): Promise<GroupResponse> {
		try {
			return await this.patchJson<GroupResponse>(`/api/groups/${id}`, data);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async deleteGroup(id: string): Promise<{ success: boolean }> {
		try {
			return await this.deleteJson<{ success: boolean }>(`/api/groups/${id}`);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async getStrategy(): Promise<StrategyName> {
		const data = await this.getJson<StrategyResponse>("/api/config/strategy");
		return data.strategy;
	}

	async listStrategies(): Promise<StrategyName[]> {
		return this.getJson<StrategyName[]>("/api/strategies");
	}

	async setStrategy(strategy: StrategyName): Promise<void> {
		try {
			await this.postJson<MutationResult<StrategyResponse>>(
				"/api/config/strategy",
				{ strategy },
			);
		} catch (error) {
			if (error instanceof HttpError) {
				throw new Error(error.message);
			}
			throw error;
		}
	}

	async getRetention(): Promise<RetentionGetResponse> {
		return this.getJson<RetentionGetResponse>("/api/config/retention");
	}

	async setRetention(partial: RetentionSetRequest): Promise<void> {
		await this.postJson("/api/config/retention", partial);
	}

	async cleanupNow(): Promise<CleanupResponse> {
		const result = await this.postJson<MutationResult<CleanupResponse>>(
			"/api/maintenance/cleanup",
		);
		return (
			result.data ?? { removedRequests: 0, removedPayloads: 0, cutoffIso: "" }
		);
	}

	async compactDb(): Promise<void> {
		await this.postJson("/api/maintenance/compact");
	}
}

export const api = new API();
